/**
 * Event Hotel data
 */

import { mapToArray } from 'nucleus-widgets/utils/arrayToMap';
import removeKeys from 'nucleus-core/utils/removeKeys';
import { getIn } from 'icepick';
import { getLocalDateFromEventDateString } from './dateUtils';
import { defaultRegistrationTypeId } from './registrationType';

/**
 * Utility function to perform necessary transformations renaming parameters needed on UI
 * TODO : We can remove this method usage on initial load once code sweep on UI is done.
 * @param {Array} hotels
 * @returns {Object[]}
 */
export function transformHotelData(hotels) {
  return mapToArray(hotels).filter(hotel => hotel && hotel.hotelRooms).map(hotel => {
    return {
      ...removeKeys(hotel, ['hotelRooms', 'order']),
      roomTypes: mapToArray(hotel.hotelRooms)
        .filter(hotelRoom => hotelRoom && hotelRoom.roomDetailsByDateAndRegType)
        .map(hotelRoom => {
          return {
            ...removeKeys(hotelRoom, ['roomDetailsByDateAndRegType', 'name', 'minimumRequiredNights']),
            roomRate: mapToArray(hotelRoom.roomDetailsByDateAndRegType)
              .filter(roomDetailByDateAndRegType => !!roomDetailByDateAndRegType)
              .map(roomDetailByDateAndRegType => {
                return {
                  ...removeKeys(roomDetailByDateAndRegType, ['date']),
                  roomRateDate: roomDetailByDateAndRegType.date
                };
              }),
            roomTypeName: hotelRoom.name,
            minNumberOfNights: hotelRoom.minimumRequiredNights
          };
        }),
      sortOrder: hotel.order
    };
  });
}

/**
 * only transforms the hotem room dates against event timezone
 * @param hotels
 * @param eventTimezone
 * @returns {{arrivalDate: Date, departureDate: Date, roomTypes: {roomRate: {roomRateDate: Date}[]}[]}[]}
 */
export function transformHotelRoomDates(hotels, eventTimezone) {
  return mapToArray(hotels).filter(hotel => !!hotel.roomTypes).map(hotel => {
    // hotel dates are in yyyy-mm-ddTHH:MM:SS.sssZ format, we need to pull out date part (yyyy-mm-dd) from this.
    const arrivalDate = hotel.arrivalDate.substring(0, 10);
    const departureDate = hotel.departureDate.substring(0, 10);

    return {
      ...hotel,
      // Ignoring date part of hotel level setup, not used for flex events currently
      arrivalDate: getLocalDateFromEventDateString(arrivalDate, eventTimezone),
      departureDate: getLocalDateFromEventDateString(departureDate, eventTimezone),
      roomTypes: mapToArray(hotel.roomTypes).filter(room => !!room.roomRate).map(room => {
        return {
          ...room,
          roomRate: mapToArray(room.roomRate).map(ratesMap => {
            return {
              ...ratesMap,
              roomRateDate: getLocalDateFromEventDateString(ratesMap.roomRateDate, eventTimezone)
            };
          })
        };
      })
    };
  });
}

/**
 * Utility function to traverse to room rate level and refresh capacity property in hotel room per date
 * based on capacity map.
 * @param {Array} hotels
 * @param {Object} capacityMap
 * @param {Boolean} isInTestMode
 * @param {Boolean} isPlanner
 * @returns {any[]}
 */
export function addHotelsRoomsCapacityProperty(hotels, capacityMap, isInTestMode, isPlanner) {
  return mapToArray(hotels).map(hotel => {
    let modifiedHotel = { ...hotel };
    modifiedHotel.roomTypes = mapToArray(hotel.roomTypes)
      .map(hotelRoom => {
        let modifiedHotelRoom = { ...hotelRoom };
        modifiedHotelRoom.roomRate = mapToArray(hotelRoom.roomRate)
          .map(roomDetailByDateAndRegType => {
            return setHasCapacityFlag(roomDetailByDateAndRegType, capacityMap, isInTestMode, isPlanner);
          });
        return modifiedHotelRoom;
      });
    return modifiedHotel;
  });
}

/**
 * Utility function to set/modify capacity property in hotel room per date based on capacity map.
 * @param {Object} roomRateByDateAndRegType
 * @param {Object} capacityMap
 * @param {Boolean} isInTestMode
 * @param {Boolean} isPlanner
 * @returns {Object}
 */
function setHasCapacityFlag(roomRateByDateAndRegType, capacityMap, isInTestMode, isPlanner) {
  let modifiedRoomRateByDateAndRegType = { ...roomRateByDateAndRegType };
  // allow planner to override capacities
  if (!isPlanner) {
    if (capacityMap && roomRateByDateAndRegType.capacityId) {
      // To use test mode capacity documents in case of test mode
      let capacityKey = isInTestMode ? roomRateByDateAndRegType.capacityId + '_testmode' :
        roomRateByDateAndRegType.capacityId;
      modifiedRoomRateByDateAndRegType.hasCapacity = capacityMap[capacityKey] &&
        (capacityMap[capacityKey].availableCapacity > 0);
      modifiedRoomRateByDateAndRegType.availableCapacity = capacityMap[capacityKey] ?
        capacityMap[capacityKey].availableCapacity : 0;
    } else {
      modifiedRoomRateByDateAndRegType.hasCapacity = false;
      modifiedRoomRateByDateAndRegType.availableCapacity = 0; // can remove this since we can not select such rooms
    }
  } else {
    modifiedRoomRateByDateAndRegType.hasCapacity = true;
  }
  return modifiedRoomRateByDateAndRegType;
}

/**
 * Gets the first hotel which has rooms present in it
 * @param {*} eventTravel
 */
export function findFirstHotelWithRooms(eventTravel) {
  const hotels = getIn(eventTravel, ['hotelsData', 'hotels']);
  if (hotels && hotels.length > 0) {
    const hotelsWithRooms = hotels.filter(hotel => hotel.roomTypes && hotel.roomTypes.length > 0);
    return getIn(hotelsWithRooms, ['0']);
  }
  return undefined;
}

/**
 * filters room rate data by reg types. If data not present for a reg type, filters based on default reg type
 * @param roomRateData
 * @param registrationTypeIds
 * @returns {Array}
 */
export function filterRoomRatesByRegType(roomRateData, ...registrationTypeIds) {
  let roomRatesByRegType = (registrationTypeIds && registrationTypeIds.length !== 0) ?
    roomRateData.filter(rate => registrationTypeIds.includes(rate.contactTypeId)) : [];

  if (!roomRatesByRegType || !roomRatesByRegType.length) {
    roomRatesByRegType = roomRateData.filter(rate => rate.contactTypeId === defaultRegistrationTypeId);
  }
  return roomRatesByRegType;
}

export function transformHotelAdvanceRules(hotelAdvanceRules = [], eventTimezone) {
  return hotelAdvanceRules.map(rule => {
    return {
      ...rule,
      arrivalDate: rule.arrivalDate && getLocalDateFromEventDateString(rule.arrivalDate, eventTimezone),
      departureDate: rule.departureDate && getLocalDateFromEventDateString(rule.departureDate, eventTimezone),
      defaultArrivalDate: rule.defaultArrivalDate && getLocalDateFromEventDateString(rule.defaultArrivalDate,
        eventTimezone),
      defaultDepartureDate: rule.defaultDepartureDate && getLocalDateFromEventDateString(rule.defaultDepartureDate,
        eventTimezone)
    };
  });
}
